<template>
  <div
    class="org-page agent-config-tab-pane other-settings-tab"
    :class="{ 'other-settings-tab--embedded': embedded }"
  >
    <SnackbarComp v-if="!embedded" :SnackbarComponent="SnackbarComponent" />

    <header
      v-if="!embedded"
      class="other-settings-tab__header d-flex align-center justify-space-between flex-wrap ga-3"
    >
      <div class="min-w-0">
        <h3 class="other-settings-tab__title mb-0">Other settings</h3>
        <p class="other-settings-tab__subtitle mb-0">
          Saved on organisation details as chatbot settings
        </p>
      </div>
      <button
        type="button"
        class="asx-save"
        :class="{ 'asx-save--busy': saving }"
        :disabled="saveButtonDisabled"
        @click="submit"
      >
        <span v-if="saving" class="asx-save__spinner" aria-hidden="true" />
        <v-icon v-else icon="mdi-content-save-outline" size="16" />
        Save
      </button>
    </header>

    <div class="other-settings-tab__form-wrap asx">
      <div class="asx__mesh" aria-hidden="true" />
      <div class="asx__grain" aria-hidden="true" />

      <form class="asx__canvas" ref="canvas" @submit.prevent="submit">
        <header class="asx__topbar">
          <div class="asx__topbar-copy">
            <span class="asx__kicker">Agent studio</span>
            <h2 class="asx__headline">Configure your AI employee</h2>
          </div>
          <button
            v-if="embedded"
            type="button"
            class="asx-save asx-save--hero"
            :class="{ 'asx-save--busy': saving }"
            :disabled="saveButtonDisabled"
            @click="submit"
          >
            <span v-if="saving" class="asx-save__spinner" aria-hidden="true" />
            <v-icon v-else icon="mdi-check-circle-outline" size="17" />
            Save changes
          </button>
        </header>

        <section class="asx__persona" aria-label="Agent identity">
          <div class="asx__persona-stage">
            <div class="asx__orbit" aria-hidden="true">
              <span class="asx__orbit-ring asx__orbit-ring--a" />
              <span class="asx__orbit-ring asx__orbit-ring--b" />
              <span class="asx__orbit-core">{{ agentInitials }}</span>
            </div>

            <div class="asx__persona-editor">
              <label class="asx__micro-label" for="asx-agent-name">
                Agent name
              </label>
              <input
                id="asx-agent-name"
                v-model="form.chatbot_name"
                type="text"
                class="asx__name-input"
                :class="{ 'asx__name-input--error': showValidation && !form.chatbot_name?.trim() }"
                placeholder="Name your agent..."
                autocomplete="off"
                :disabled="!isSessionReady || saving"
              />
              <p
                v-if="showValidation && !form.chatbot_name?.trim()"
                class="asx__field-error"
              >
                Agent name is required
              </p>
              <p class="asx__name-hint">
                Include this name in your system prompt so callers hear it on calls.
              </p>
            </div>

            <div class="asx__persona-status">
              <span class="asx__live-dot" aria-hidden="true" />
              Live persona
            </div>
          </div>

          <div class="asx__persona-controls">
            <div class="asx__control-block">
              <span class="asx__micro-label">Voice persona</span>
              <div
                class="asx__gender-deck"
                role="radiogroup"
                aria-label="Agent gender"
              >
                <button
                  v-for="option in genderDeck"
                  :key="option.value"
                  type="button"
                  role="radio"
                  class="asx__gender-card"
                  :class="[
                    `asx__gender-card--${option.tone}`,
                    {
                      'asx__gender-card--active':
                        form.chatbot_gender === option.value,
                    },
                  ]"
                  :aria-checked="form.chatbot_gender === option.value"
                  :disabled="!isSessionReady || saving"
                  @click="selectGender(option.value)"
                >
                  <span class="asx__gender-glyph" aria-hidden="true">
                    <v-icon :icon="option.icon" size="22" />
                  </span>
                  <span class="asx__gender-label">{{ option.label }}</span>
                  <span class="asx__gender-check" aria-hidden="true">
                    <v-icon icon="mdi-check" size="14" />
                  </span>
                </button>
              </div>
              <p
                v-if="showValidation && !form.chatbot_gender"
                class="asx__field-error"
              >
                Select a voice persona
              </p>
            </div>

            <div v-if="showLanguageField" class="asx__control-block">
              <span class="asx__micro-label">Conversation language</span>
              <div class="asx__lang-rail" role="listbox" aria-label="Agent language">
                <button
                  v-for="option in languageOptions"
                  :key="option.value"
                  type="button"
                  role="option"
                  class="asx__lang-pill"
                  :class="{
                    'asx__lang-pill--active':
                      form.chatbot_language === option.value,
                  }"
                  :aria-selected="form.chatbot_language === option.value"
                  :disabled="!isSessionReady || saving"
                  @click="selectLanguage(option.value)"
                >
                  <span class="asx__lang-code">{{ languageShortCode(option.value) }}</span>
                  <span class="asx__lang-name">{{ option.label }}</span>
                </button>
              </div>
              <p
                v-if="showValidation && !form.chatbot_language"
                class="asx__field-error"
              >
                Select a language
              </p>
            </div>
          </div>
        </section>

        <!-- Temporarily hidden — Greeting & system prompt (formatGreetingMessage / formatSystemPrompt)
        <section class="asx__prompts" aria-label="Agent instructions">
          <div class="asx__section-head">
            <div>
              <span class="asx__kicker">Instructions</span>
              <h3 class="asx__section-title">Greeting &amp; system prompt</h3>
            </div>
          </div>

          <div class="asx__prompt-grid">
            <div class="asx__prompt-studio">
              <div class="asx__prompt-head">
                <div>
                  <span class="asx__micro-label">Greeting message</span>
                  <p class="asx__voice-sub">
                    What your agent says when answering a call.
                  </p>
                </div>
                <button
                  type="button"
                  class="asx__format-btn"
                  :class="{ 'asx__format-btn--loading': formattingGreeting }"
                  :disabled="formatGreetingDisabled"
                  @click="onFormatGreeting"
                >
                  <span v-if="formattingGreeting" class="asx-save__spinner" />
                  <v-icon v-else icon="mdi-auto-fix" size="16" />
                  Format
                </button>
              </div>

              <textarea
                v-model="form.greeting_message"
                class="asx__voice-input asx__prompt-input"
                rows="5"
                placeholder="Write how your agent should greet callers..."
                :disabled="!isSessionReady || saving || formattingGreeting || savingGreeting"
              />

              <div v-if="greetingSaveAvailable" class="asx__prompt-actions">
                <button
                  type="button"
                  class="asx__prompt-save"
                  :class="{ 'asx__prompt-save--busy': savingGreeting }"
                  :disabled="saveGreetingDisabled"
                  @click="saveGreetingMessage"
                >
                  <span v-if="savingGreeting" class="asx-save__spinner" />
                  <v-icon v-else icon="mdi-content-save-outline" size="16" />
                  Save greeting
                </button>
              </div>
            </div>

            <div class="asx__prompt-studio">
              <div class="asx__prompt-head">
                <div>
                  <span class="asx__micro-label">System prompt</span>
                  <p class="asx__voice-sub">
                    Core behaviour and personality instructions for your agent.
                  </p>
                </div>
                <button
                  type="button"
                  class="asx__format-btn"
                  :class="{ 'asx__format-btn--loading': formattingSystemPrompt }"
                  :disabled="formatSystemPromptDisabled"
                  @click="onFormatSystemPrompt"
                >
                  <span v-if="formattingSystemPrompt" class="asx-save__spinner" />
                  <v-icon v-else icon="mdi-auto-fix" size="16" />
                  Format
                </button>
              </div>

              <textarea
                v-model="form.system_prompt"
                class="asx__voice-input asx__prompt-input"
                rows="8"
                placeholder="Describe how your agent should behave, respond, and represent your business..."
                :disabled="!isSessionReady || saving || formattingSystemPrompt || savingSystemPrompt"
              />

              <div v-if="systemPromptSaveAvailable" class="asx__prompt-actions">
                <button
                  type="button"
                  class="asx__prompt-save"
                  :class="{ 'asx__prompt-save--busy': savingSystemPrompt }"
                  :disabled="saveSystemPromptDisabled"
                  @click="saveSystemPrompt"
                >
                  <span v-if="savingSystemPrompt" class="asx-save__spinner" />
                  <v-icon v-else icon="mdi-content-save-outline" size="16" />
                  Save system prompt
                </button>
              </div>
            </div>
          </div>
        </section>
        -->

        <div class="asx__security-group">
          <span class="asx__kicker">Security layer</span>

        <section class="asx__access" aria-label="Call access">
          <div class="asx__section-head">
            <div>
              <h3 class="asx__section-title">For incoming calls</h3>
            </div>
            <span
              class="asx__access-badge"
              :class="
                form.restrict_incoming_calls
                  ? 'asx__access-badge--locked'
                  : 'asx__access-badge--open'
              "
            >
              {{ form.restrict_incoming_calls ? 'Whitelist mode' : 'Open mode' }}
            </span>
          </div>

          <div class="asx__mode-deck">
            <button
              type="button"
              class="asx__mode-card"
              :class="{ 'asx__mode-card--active': !form.restrict_incoming_calls }"
              :disabled="!isSessionReady || saving"
              @click="setAccessMode(false)"
            >
              <span class="asx__mode-art asx__mode-art--open" aria-hidden="true">
                <v-icon icon="mdi-earth" size="26" />
              </span>
              <span class="asx__mode-copy">
                <span class="asx__mode-title">Open access</span>
                <span class="asx__mode-desc">
                  Any caller can connect with your agent instantly.
                </span>
              </span>
              <span class="asx__mode-indicator" aria-hidden="true" />
            </button>

            <button
              type="button"
              class="asx__mode-card"
              :class="{ 'asx__mode-card--active': form.restrict_incoming_calls }"
              :disabled="!isSessionReady || saving"
              @click="setAccessMode(true)"
            >
              <span class="asx__mode-art asx__mode-art--locked" aria-hidden="true">
                <v-icon icon="mdi-shield-lock-outline" size="26" />
              </span>
              <span class="asx__mode-copy">
                <span class="asx__mode-title">Whitelist only</span>
                <span class="asx__mode-desc">
                  Only verified numbers from your Callers list get through.
                </span>
              </span>
              <span class="asx__mode-indicator" aria-hidden="true" />
            </button>
          </div>

          <Transition name="asx-reveal">
            <div
              v-if="form.restrict_incoming_calls"
              ref="incomingRevealPanel"
              class="asx__voice-studio"
            >
              <div class="asx__voice-head">
                <div>
                  <span class="asx__micro-label">Rejection message</span>
                  <p class="asx__voice-sub">
                    Spoken to callers who are not on your whitelist.
                  </p>
                </div>
                <div class="asx__wave" aria-hidden="true">
                  <span v-for="n in 12" :key="n" class="asx__wave-bar" />
                </div>
              </div>

              <textarea
                v-model="form.restricted_caller_audio_text"
                class="asx__voice-input"
                :class="{
                  'asx__voice-input--error':
                    showValidation &&
                    form.restrict_incoming_calls &&
                    !form.restricted_caller_audio_text?.trim(),
                }"
                rows="4"
                placeholder="Craft the message your agent speaks to blocked callers..."
                :disabled="!isSessionReady || saving || convertingAudio"
              />

              <p
                v-if="
                  showValidation &&
                  form.restrict_incoming_calls &&
                  !form.restricted_caller_audio_text?.trim()
                "
                class="asx__field-error"
              >
                Rejection message is required
              </p>

              <p v-if="needsAudioConversion" class="asx__voice-warning">
                Synthesize audio before saving changes.
              </p>

              <div class="asx__voice-actions">
                <button
                  type="button"
                  class="asx__synth-btn"
                  :class="{ 'asx__synth-btn--loading': convertingAudio }"
                  :disabled="
                    !isSessionReady ||
                    saving ||
                    convertingAudio ||
                    !form.restricted_caller_audio_text?.trim()
                  "
                  @click="convertToAudio"
                >
                  <span v-if="convertingAudio" class="asx-save__spinner" />
                  <v-icon v-else icon="mdi-waveform" size="18" />
                  Synthesize voice
                </button>

                <audio
                  v-if="hasAudioPreview"
                  :key="audioPreviewKey"
                  ref="audioPreview"
                  :src="restrictedAudioUrl"
                  controls
                  preload="auto"
                  class="asx__audio-player"
                >
                  Your browser does not support audio playback.
                </audio>
              </div>
            </div>
          </Transition>
        </section>

        <section class="asx__access" aria-label="Outbound call access" ref="outboundAccessSection">
          <div class="asx__section-head">
            <div>
              <h3 class="asx__section-title">For outgoing calls</h3>
            </div>
            <span
              class="asx__access-badge"
              :class="
                form.restrict_outbound_to_known_callers
                  ? 'asx__access-badge--locked'
                  : 'asx__access-badge--open'
              "
            >
              {{ form.restrict_outbound_to_known_callers ? 'Whitelist mode' : 'Open mode' }}
            </span>
          </div>

          <div class="asx__mode-deck">
            <button
              type="button"
              class="asx__mode-card"
              :class="{ 'asx__mode-card--active': !form.restrict_outbound_to_known_callers }"
              :disabled="!isSessionReady || saving"
              @click="setOutboundAccessMode(false)"
            >
              <span class="asx__mode-art asx__mode-art--open" aria-hidden="true">
                <v-icon icon="mdi-phone-forward-outline" size="26" />
              </span>
              <span class="asx__mode-copy">
                <span class="asx__mode-title">Dial everyone</span>
                <span class="asx__mode-desc">
                  Every campaign can call any lead in its own list, as usual.
                </span>
              </span>
              <span class="asx__mode-indicator" aria-hidden="true" />
            </button>

            <button
              type="button"
              class="asx__mode-card"
              :class="{ 'asx__mode-card--active': form.restrict_outbound_to_known_callers }"
              :disabled="!isSessionReady || saving"
              @click="setOutboundAccessMode(true)"
            >
              <span class="asx__mode-art asx__mode-art--locked" aria-hidden="true">
                <v-icon icon="mdi-shield-lock-outline" size="26" />
              </span>
              <span class="asx__mode-copy">
                <span class="asx__mode-title">Whitelist only</span>
                <span class="asx__mode-desc">
                  Overrides every campaign -- no outbound call goes out to a number
                  that isn't already in your Callers list.
                </span>
              </span>
              <span class="asx__mode-indicator" aria-hidden="true" />
            </button>
          </div>
        </section>
        </div>

        <section class="asx__access" aria-label="Call recording disclaimer">
          <div class="asx__section-head">
            <div>
              <span class="asx__kicker">Compliance</span>
              <h3 class="asx__section-title">Call recording disclaimer</h3>
            </div>
            <v-switch
              v-model="form.disclaimer_enabled"
              color="primary"
              density="comfortable"
              hide-details
              :disabled="!isSessionReady || saving"
              @update:model-value="onDisclaimerToggle"
            />
          </div>

          <Transition name="asx-reveal">
            <div v-if="form.disclaimer_enabled" class="asx__voice-studio">
              <div class="asx__voice-head">
                <div>
                  <span class="asx__micro-label">Disclaimer text</span>
                  <p class="asx__voice-sub">
                    Spoken by your agent at the very start of every call.
                  </p>
                </div>
              </div>

              <textarea
                v-model="form.disclaimer_text"
                class="asx__voice-input"
                :class="{
                  'asx__voice-input--error':
                    showValidation &&
                    form.disclaimer_enabled &&
                    !form.disclaimer_text?.trim(),
                }"
                rows="3"
                placeholder="This call is being recorded for training and quality purposes."
                :disabled="!isSessionReady || saving || loadingDisclaimerDefault"
              />

              <p
                v-if="
                  showValidation &&
                  form.disclaimer_enabled &&
                  !form.disclaimer_text?.trim()
                "
                class="asx__field-error"
              >
                Disclaimer text is required
              </p>
            </div>
          </Transition>
        </section>

        <section class="asx__access" aria-label="Business hours">
          <div class="asx__section-head">
            <div>
              <span class="asx__kicker">Call redirect</span>
              <h3 class="asx__section-title">Business hours</h3>
              <p class="asx__voice-sub">
                Team members are only redirected calls within these hours. If
                everyone's outside their hours, the caller hears the message
                below instead.
              </p>
            </div>
          </div>

          <div class="asx__voice-studio">
            <div class="tk-dialog__field-row" style="display:flex; gap:16px;">
              <div class="tk-dialog__field" style="flex:1;">
                <label class="asx__micro-label">Start</label>
                <input
                  v-model="form.business_hours_start"
                  type="time"
                  class="asx__voice-input asx__voice-input--compact"
                  :disabled="!isSessionReady || saving"
                />
              </div>
              <div class="tk-dialog__field" style="flex:1;">
                <label class="asx__micro-label">End</label>
                <input
                  v-model="form.business_hours_end"
                  type="time"
                  class="asx__voice-input asx__voice-input--compact"
                  :disabled="!isSessionReady || saving"
                />
              </div>
            </div>

            <div class="asx__voice-head" style="margin-top:16px;">
              <span class="asx__micro-label">Out of hours message</span>
            </div>
            <textarea
              v-model="form.out_of_hours_message"
              class="asx__voice-input"
              rows="3"
              placeholder="Thank you for calling. Our team is currently outside business hours."
              :disabled="!isSessionReady || saving"
            />
          </div>
        </section>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import organizationDisplayMixin from '@/mixins/Organization/organizationDisplayMixin'
import updateOrganization from '@/mixins/Organization/updateOrganization'
import convertTextToSpeechMixin, {
  extractTextAudioUrl,
} from '@/mixins/Organization/convertTextToSpeech'
import formatAgentPromptsMixin, {
  extractFormattedGreetingMessage,
  extractFormattedSystemPrompt,
} from '@/mixins/Organization/formatAgentPrompts'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  readChatbotDetailsFromOrg,
  buildChatbotDetailsInput,
  readRestrictedCallerAudioTextFromOrg,
  readTextAudioUrlFromOrg,
  readGreetingMessageFromOrg,
  readSystemPromptFromOrg,
  CHATBOT_GENDER_OPTIONS,
  getAgentLanguageOptionsForOrg,
  orgSupportsChatbotLanguage,
  DEFAULT_RESTRICTED_CALLER_AUDIO_TEXT,
} from '@/utils/agentOtherSettings'
import { parseApiBoolean } from '@/utils/format'
import { fetchPlatformDisclaimerDefault } from '@/api/orgSettingsApi'

const GENDER_ICON_MAP = {
  MALE: { icon: 'mdi-gender-male', tone: 'blue', label: 'Masculine' },
  FEMALE: { icon: 'mdi-gender-female', tone: 'rose', label: 'Feminine' },
}

const defaultForm = () => ({
  chatbot_name: '',
  chatbot_gender: '',
  chatbot_language: '',
  restrict_incoming_calls: false,
  restricted_caller_audio_text: DEFAULT_RESTRICTED_CALLER_AUDIO_TEXT,
  restrict_outbound_to_known_callers: false,
  greeting_message: '',
  system_prompt: '',
  disclaimer_enabled: false,
  disclaimer_text: '',
  business_hours_start: '10:00',
  business_hours_end: '19:00',
  out_of_hours_message: '',
})

export default {
  name: 'OtherSettingsTab',
  props: {
    active: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  emits: ['save-state', 'snackbar'],
  components: { SnackbarComp },
  mixins: [
    organizationDisplayMixin,
    updateOrganization,
    convertTextToSpeechMixin,
    formatAgentPromptsMixin,
  ],
  data() {
    return {
      SnackbarComponent: {},
      form: defaultForm(),
      showValidation: false,
      saving: false,
      convertingAudio: false,
      formattingGreeting: false,
      formattingSystemPrompt: false,
      greetingSaveAvailable: false,
      systemPromptSaveAvailable: false,
      savingGreeting: false,
      savingSystemPrompt: false,
      restrictedAudioUrl: '',
      audioPreviewKey: 0,
      shouldAutoPlayAudio: false,
      skipOrgSync: false,
      lastConvertedAudioText: '',
      loadingDisclaimerDefault: false,
    }
  },
  computed: {
    ...mapGetters(['sessionContext']),
    genderDeck() {
      return CHATBOT_GENDER_OPTIONS.map((option) => ({
        value: option.value,
        label: GENDER_ICON_MAP[option.value]?.label ?? option.label,
        icon: GENDER_ICON_MAP[option.value]?.icon ?? 'mdi-gender-male-female',
        tone: GENDER_ICON_MAP[option.value]?.tone ?? 'violet',
      }))
    },
    showLanguageField() {
      return orgSupportsChatbotLanguage(this.displayOrganization)
    },
    languageOptions() {
      return getAgentLanguageOptionsForOrg(this.displayOrganization)
    },
    agentInitials() {
      const name = String(this.form.chatbot_name ?? '').trim()
      if (!name) return 'AI'
      const parts = name.split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      }
      return name.slice(0, 2).toUpperCase()
    },
    hasAudioPreview() {
      return Boolean(this.restrictedAudioUrl)
    },
    needsAudioConversion() {
      if (!this.form.restrict_incoming_calls) return false

      const text = this.form.restricted_caller_audio_text?.trim() || ''
      if (!text) return true
      if (!this.restrictedAudioUrl) return true

      return text !== this.lastConvertedAudioText.trim()
    },
    isFormValid() {
      if (!this.form.chatbot_name?.trim()) return false
      if (!this.form.chatbot_gender) return false
      if (this.showLanguageField && !this.form.chatbot_language) return false
      if (
        this.form.restrict_incoming_calls &&
        !this.form.restricted_caller_audio_text?.trim()
      ) {
        return false
      }
      if (this.form.disclaimer_enabled && !this.form.disclaimer_text?.trim()) {
        return false
      }
      return true
    },
    canSave() {
      return (
        this.isSessionReady &&
        this.isFormValid &&
        !this.saving &&
        !this.convertingAudio &&
        !this.needsAudioConversion
      )
    },
    saveButtonDisabled() {
      return (
        !this.isSessionReady ||
        this.saving ||
        this.convertingAudio ||
        this.formattingGreeting ||
        this.formattingSystemPrompt ||
        this.savingGreeting ||
        this.savingSystemPrompt
      )
    },
    saveGreetingDisabled() {
      return (
        !this.isSessionReady ||
        this.saving ||
        this.savingGreeting ||
        this.formattingGreeting ||
        !this.form.greeting_message?.trim()
      )
    },
    saveSystemPromptDisabled() {
      return (
        !this.isSessionReady ||
        this.saving ||
        this.savingSystemPrompt ||
        this.formattingSystemPrompt ||
        !this.form.system_prompt?.trim()
      )
    },
    formatGreetingDisabled() {
      return (
        !this.isSessionReady ||
        this.saving ||
        this.formattingGreeting ||
        this.formattingSystemPrompt ||
        !this.form.greeting_message?.trim() ||
        !this.form.chatbot_name?.trim() ||
        !this.displayOrganization?.org_name?.trim() ||
        (this.showLanguageField && !this.form.chatbot_language)
      )
    },
    formatSystemPromptDisabled() {
      return (
        !this.isSessionReady ||
        this.saving ||
        this.formattingGreeting ||
        this.formattingSystemPrompt ||
        !this.form.system_prompt?.trim() ||
        !this.form.chatbot_name?.trim() ||
        !this.displayOrganization?.org_name?.trim() ||
        (this.showLanguageField && !this.form.chatbot_language)
      )
    },
  },
  watch: {
    saving: 'emitSaveState',
    canSave: 'emitSaveState',
    convertingAudio: 'emitSaveState',
    formattingGreeting: 'emitSaveState',
    formattingSystemPrompt: 'emitSaveState',
    isFormValid: 'emitSaveState',
    'form.restricted_caller_audio_text'(value) {
      if (this.skipOrgSync || !this.form.restrict_incoming_calls) return

      const trimmed = String(value ?? '').trim()
      if (trimmed !== this.lastConvertedAudioText.trim()) {
        this.restrictedAudioUrl = ''
        this.shouldAutoPlayAudio = false
      } else if (trimmed) {
        this.reloadAudioFromOrg(this.displayOrganization)
      }
    },
    'form.restrict_incoming_calls'(enabled) {
      if (!enabled) {
        this.lastConvertedAudioText = ''
        return
      }

      const org = this.displayOrganization
      const orgText = readRestrictedCallerAudioTextFromOrg(org)
      const orgAudioUrl = readTextAudioUrlFromOrg(org)
      const formText = this.form.restricted_caller_audio_text?.trim() || ''

      if (orgAudioUrl && orgText && orgText === formText) {
        this.lastConvertedAudioText = orgText
        this.reloadAudioFromOrg(org)
      }
    },
    restrictedAudioUrl(url) {
      if (url && this.shouldAutoPlayAudio) {
        this.shouldAutoPlayAudio = false
        this.playAudioPreview()
      }
    },
    displayOrganization: {
      immediate: true,
      deep: true,
      handler(org) {
        if (!this.active || this.skipOrgSync) return
        this.syncFormFromOrg(org)
      },
    },
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.onTabActivated()
      },
    },
  },
  mounted() {
    this.emitSaveState()
  },
  methods: {
    showSnack(color, text) {
      if (this.embedded) {
        this.$emit('snackbar', { color, text })
        return
      }

      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: color,
        SnackbarText: text,
      }
    },
    emitSaveState() {
      this.$emit('save-state', {
        saving: this.saving,
        canSave: this.canSave,
      })
    },
    languageShortCode(value) {
      return String(value ?? '')
        .split('-')[0]
        .slice(0, 2)
        .toUpperCase()
    },
    selectGender(value) {
      this.form.chatbot_gender = value
    },
    selectLanguage(value) {
      this.form.chatbot_language = value
    },
    setAccessMode(restricted) {
      // Whitelist mode reveals a rejection-message panel right below this
      // card deck. That insertion happens INSIDE the current scroll
      // position (not above it), so scrollTop itself never moves -- what
      // actually happens is everything after the panel, starting with
      // "For outgoing calls", shifts down by the panel's own height in
      // absolute layout terms, which silently pushes it out of the same
      // viewport window. scrollIntoView (tried previously) doesn't fix
      // this reliably: its 'smooth' animation runs AFTER the shift has
      // already painted, so there's a visible instant where the section
      // is genuinely off-screen before the scroll catches up -- which is
      // exactly the "it goes off" report. Compensate scrollTop by the
      // panel's own measured height instead, synchronously in the same
      // tick as the DOM patch (before the browser paints), so nothing
      // below the toggle ever visibly moves at all.
      const canvas = this.$refs.canvas
      const wasRestricted = this.form.restrict_incoming_calls
      if (restricted === wasRestricted) return
      const scrollTopBefore = canvas ? canvas.scrollTop : 0
      const panelHeightBefore = this.$refs.incomingRevealPanel?.offsetHeight || 0
      this.form.restrict_incoming_calls = restricted
      if (!canvas) return
      this.$nextTick(() => {
        if (restricted) {
          const panelHeightAfter = this.$refs.incomingRevealPanel?.offsetHeight || 0
          if (panelHeightAfter) canvas.scrollTop = scrollTopBefore + panelHeightAfter
        } else if (panelHeightBefore) {
          canvas.scrollTop = Math.max(0, scrollTopBefore - panelHeightBefore)
        }
      })
    },
    setOutboundAccessMode(restricted) {
      this.form.restrict_outbound_to_known_callers = restricted
    },
    buildFormatPromptContext() {
      const { current_user_id } = this.sessionContext ?? {}
      const orgName = String(this.displayOrganization?.org_name ?? '').trim()
      const chatbotName = String(this.form.chatbot_name ?? '').trim()
      const chatbotLanguage = String(this.form.chatbot_language ?? '').trim()

      return {
        current_user_id,
        org_name: orgName,
        chatbot_name: chatbotName,
        chatbot_language: chatbotLanguage,
      }
    },
    async onFormatGreeting() {
      const greeting = this.form.greeting_message?.trim()
      if (!greeting) {
        this.showSnack('error', 'Enter a greeting message before formatting.')
        return
      }

      const { current_user_id, org_name, chatbot_name, chatbot_language } =
        this.buildFormatPromptContext()

      if (!current_user_id) {
        this.onError(null, 'Session is not ready.')
        return
      }
      if (!org_name || !chatbot_name) {
        this.showSnack('error', 'Agent name and organisation name are required.')
        return
      }
      if (this.showLanguageField && !chatbot_language) {
        this.showSnack('error', 'Select a conversation language before formatting.')
        return
      }

      this.formattingGreeting = true
      try {
        const response = await this.formatGreetingMessage({
          current_user_id,
          org_name,
          chatbot_name,
          chatbot_language,
          greeting,
        })

        if (
          response &&
          typeof response === 'object' &&
          !isApiSuccess(response)
        ) {
          this.onError(response, 'Failed to format greeting message.')
          return
        }

        const formatted = extractFormattedGreetingMessage(response)
        if (!formatted) {
          this.onError(response, 'Formatted greeting was not returned.')
          return
        }

        this.form.greeting_message = formatted
        this.greetingSaveAvailable = true
        this.showSnack('success', 'Greeting formatted. Review and save when ready.')
      } catch (err) {
        this.onError(err, 'Failed to format greeting message.')
      } finally {
        this.formattingGreeting = false
      }
    },
    async onFormatSystemPrompt() {
      const systemPrompt = this.form.system_prompt?.trim()
      if (!systemPrompt) {
        this.showSnack('error', 'Enter a system prompt before formatting.')
        return
      }

      const { current_user_id, org_name, chatbot_name, chatbot_language } =
        this.buildFormatPromptContext()

      if (!current_user_id) {
        this.onError(null, 'Session is not ready.')
        return
      }
      if (!org_name || !chatbot_name) {
        this.showSnack('error', 'Agent name and organisation name are required.')
        return
      }
      if (this.showLanguageField && !chatbot_language) {
        this.showSnack('error', 'Select a conversation language before formatting.')
        return
      }

      this.formattingSystemPrompt = true
      try {
        const response = await this.formatSystemPrompt({
          current_user_id,
          org_name,
          chatbot_name,
          chatbot_language,
          system_prompt: systemPrompt,
        })

        if (
          response &&
          typeof response === 'object' &&
          !isApiSuccess(response)
        ) {
          this.onError(response, 'Failed to format system prompt.')
          return
        }

        const formatted = extractFormattedSystemPrompt(response)
        if (!formatted) {
          this.onError(response, 'Formatted system prompt was not returned.')
          return
        }

        this.form.system_prompt = formatted
        this.systemPromptSaveAvailable = true
        this.showSnack('success', 'System prompt formatted. Review and save when ready.')
      } catch (err) {
        this.onError(err, 'Failed to format system prompt.')
      } finally {
        this.formattingSystemPrompt = false
      }
    },
    onTabActivated() {
      if (!this.isSessionReady) return
      this.syncFormFromOrg(this.displayOrganization)
    },
    async saveGreetingMessage() {
      const greeting_message = this.form.greeting_message?.trim()
      if (!greeting_message) {
        this.showSnack('error', 'Greeting message is required.')
        return
      }

      const { org_id } = this.sessionContext ?? {}
      if (!org_id) {
        this.onError(null, 'Session is not ready.')
        return
      }

      this.savingGreeting = true
      try {
        const response = await this.updateOrganization({ greeting_message })
        if (!isApiSuccess(response)) {
          this.onError(response, 'Failed to save greeting message.')
          return
        }

        this.greetingSaveAvailable = false
        await this.onSaved(response, 'Greeting message saved successfully.')
      } catch (err) {
        this.onError(err, 'Failed to save greeting message.')
      } finally {
        this.savingGreeting = false
      }
    },
    async saveSystemPrompt() {
      const system_prompt = this.form.system_prompt?.trim()
      if (!system_prompt) {
        this.showSnack('error', 'System prompt is required.')
        return
      }

      const { org_id } = this.sessionContext ?? {}
      if (!org_id) {
        this.onError(null, 'Session is not ready.')
        return
      }

      this.savingSystemPrompt = true
      try {
        const response = await this.updateOrganization({ system_prompt })
        if (!isApiSuccess(response)) {
          this.onError(response, 'Failed to save system prompt.')
          return
        }

        this.systemPromptSaveAvailable = false
        await this.onSaved(response, 'System prompt saved successfully.')
      } catch (err) {
        this.onError(err, 'Failed to save system prompt.')
      } finally {
        this.savingSystemPrompt = false
      }
    },
    async onDisclaimerToggle(enabled) {
      if (!enabled || this.form.disclaimer_text?.trim()) return
      this.loadingDisclaimerDefault = true
      const response = await fetchPlatformDisclaimerDefault()
      this.loadingDisclaimerDefault = false
      if (response?.disclaimer_text) {
        this.form.disclaimer_text = response.disclaimer_text
      }
    },
    syncFormFromOrg(org) {
      const chatbot = readChatbotDetailsFromOrg(org)
      const restrictIncomingCalls = parseApiBoolean(
        org?.restrict_incoming_calls,
        false,
      )

      const savedText = readRestrictedCallerAudioTextFromOrg(org)
      const savedAudioUrl = readTextAudioUrlFromOrg(org)

      this.form = {
        ...defaultForm(),
        chatbot_name: chatbot.chatbot_name,
        chatbot_gender: chatbot.chatbot_gender,
        chatbot_language: chatbot.chatbot_language,
        restrict_incoming_calls: restrictIncomingCalls,
        restricted_caller_audio_text:
          savedText || DEFAULT_RESTRICTED_CALLER_AUDIO_TEXT,
        restrict_outbound_to_known_callers: parseApiBoolean(
          org?.restrict_outbound_to_known_callers,
          false,
        ),
        greeting_message: readGreetingMessageFromOrg(org),
        system_prompt: readSystemPromptFromOrg(org),
        disclaimer_enabled: parseApiBoolean(org?.disclaimer_enabled, false),
        disclaimer_text: org?.disclaimer_text || '',
        business_hours_start: org?.business_hours_start || '10:00',
        business_hours_end: org?.business_hours_end || '19:00',
        out_of_hours_message: org?.out_of_hours_message || '',
      }

      this.greetingSaveAvailable = false
      this.systemPromptSaveAvailable = false

      this.reloadAudioFromOrg(org)

      if (restrictIncomingCalls && savedText && savedAudioUrl) {
        this.lastConvertedAudioText = savedText
      } else if (!restrictIncomingCalls) {
        this.lastConvertedAudioText = ''
      }
    },
    isRestrictionEnabled(org) {
      return (
        parseApiBoolean(org?.restrict_incoming_calls, false) ||
        Boolean(this.form.restrict_incoming_calls)
      )
    },
    reloadAudioFromOrg(org, options = {}) {
      const { autoPlay = false, fallbackUrl = '' } = options

      if (!this.isRestrictionEnabled(org)) {
        this.restrictedAudioUrl = ''
        this.shouldAutoPlayAudio = false
        return
      }

      const audioUrl =
        readTextAudioUrlFromOrg(org) || String(fallbackUrl ?? '').trim()

      if (!audioUrl) {
        this.restrictedAudioUrl = ''
        this.shouldAutoPlayAudio = false
        return
      }

      this.restrictedAudioUrl = audioUrl
      this.audioPreviewKey += 1
      this.shouldAutoPlayAudio = autoPlay
    },
    async refreshOrgAfterConvert(maxAttempts = 6, delayMs = 1000) {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await this.refreshOrganizationFromApi()
        if (readTextAudioUrlFromOrg(this.displayOrganization)) {
          return
        }
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
        }
      }
    },
    waitForAudioElement(timeoutMs = 4000) {
      return new Promise((resolve) => {
        const started = Date.now()
        const tick = () => {
          const el = this.$refs.audioPreview
          if (el) {
            resolve(el)
            return
          }
          if (Date.now() - started >= timeoutMs) {
            resolve(null)
            return
          }
          requestAnimationFrame(tick)
        }
        tick()
      })
    },
    waitForAudioMetadata(el, timeoutMs = 5000) {
      if (!el) return Promise.resolve(false)

      if (
        el.readyState >= HTMLMediaElement.HAVE_METADATA &&
        Number.isFinite(el.duration) &&
        el.duration > 0
      ) {
        return Promise.resolve(true)
      }

      return new Promise((resolve) => {
        let settled = false
        const finish = (ok) => {
          if (settled) return
          settled = true
          el.removeEventListener('loadedmetadata', onReady)
          el.removeEventListener('canplay', onReady)
          el.removeEventListener('error', onError)
          clearTimeout(timer)
          resolve(ok)
        }
        const onReady = () => {
          finish(Number.isFinite(el.duration) && el.duration > 0)
        }
        const onError = () => finish(false)
        const timer = setTimeout(() => finish(false), timeoutMs)

        el.addEventListener('loadedmetadata', onReady, { once: true })
        el.addEventListener('canplay', onReady, { once: true })
        el.addEventListener('error', onError, { once: true })
        el.load()
      })
    },
    async playAudioPreview() {
      await this.$nextTick()
      const el = await this.waitForAudioElement()
      if (!el || !this.restrictedAudioUrl) return

      const tryPlay = async () => {
        try {
          await el.play()
        } catch {
          // User can press play on the controls.
        }
      }

      for (let attempt = 0; attempt < 8; attempt += 1) {
        const ready = await this.waitForAudioMetadata(el, attempt === 0 ? 5000 : 3000)
        if (ready) {
          await tryPlay()
          return
        }
        if (attempt < 7) {
          await new Promise((resolve) => setTimeout(resolve, 2000))
        }
      }
    },
    buildSaveInput() {
      return {
        restrict_incoming_calls: Boolean(this.form.restrict_incoming_calls),
        restrict_outbound_to_known_callers: Boolean(this.form.restrict_outbound_to_known_callers),
        disclaimer_enabled: Boolean(this.form.disclaimer_enabled),
        disclaimer_text: this.form.disclaimer_enabled
          ? (this.form.disclaimer_text || '').trim()
          : '',
        business_hours_start: this.form.business_hours_start || '10:00',
        business_hours_end: this.form.business_hours_end || '19:00',
        out_of_hours_message: (this.form.out_of_hours_message || '').trim(),
        chatbot_details: buildChatbotDetailsInput(this.form, {
          includeLanguage: this.showLanguageField,
        }),
      }
    },
    async convertToAudio() {
      const voiceText = this.form.restricted_caller_audio_text?.trim()
      if (!voiceText) {
        this.showSnack('error', 'Enter a message before converting to audio.')
        return
      }

      const { org_id } = this.sessionContext ?? {}
      if (!org_id) {
        this.onError(null, 'Session is not ready.')
        return
      }

      this.convertingAudio = true
      try {
        const response = await this.convertTextToSpeech({
          org_id,
          voice_text: voiceText,
        })

        if (!isApiSuccess(response)) {
          this.onError(response, 'Failed to convert text to audio.')
          return
        }

        const outputUri = extractTextAudioUrl(response)
        this.skipOrgSync = true
        try {
          await this.refreshOrgAfterConvert()
          const org = this.displayOrganization

          const savedText = readRestrictedCallerAudioTextFromOrg(org)
          if (savedText) {
            this.form.restricted_caller_audio_text = savedText
          }

          this.reloadAudioFromOrg(org, {
            autoPlay: true,
            fallbackUrl: outputUri,
          })

          this.lastConvertedAudioText =
            savedText || this.form.restricted_caller_audio_text?.trim() || ''
        } finally {
          this.skipOrgSync = false
        }

        if (!this.restrictedAudioUrl) {
          this.onError(null, 'Audio URL was not returned.')
          return
        }

        this.showSnack('success', 'Audio generated successfully.')
      } catch (err) {
        this.onError(err, 'Failed to convert text to audio.')
      } finally {
        this.convertingAudio = false
      }
    },
    async submit() {
      this.showValidation = true
      if (!this.isFormValid) return

      if (this.needsAudioConversion) {
        this.showSnack('error', 'Convert the message to audio before saving.')
        return
      }

      const { org_id } = this.sessionContext ?? {}
      if (!org_id) {
        this.onError(null, 'Session is not ready.')
        return
      }

      this.saving = true
      try {
        const response = await this.updateOrganization(this.buildSaveInput())
        if (!isApiSuccess(response)) {
          this.onError(response, 'Failed to save other settings.')
          return
        }

        if (!this.form.restrict_incoming_calls) {
          this.restrictedAudioUrl = ''
          this.shouldAutoPlayAudio = false
        }

        this.showValidation = false
        await this.onSaved(response, 'Agent settings saved successfully.')
      } catch (err) {
        this.onError(err, 'Failed to save other settings.')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.agent-config-tab-pane {
  height: 100%;
  min-height: 0;
}

.other-settings-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 10px;
  padding: var(--y-space-2);
  box-sizing: border-box;
  overflow: hidden;
}

.other-settings-tab--embedded {
  padding: 0;
  gap: 0;
}

.other-settings-tab__header {
  flex-shrink: 0;
}

.other-settings-tab__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--y-text);
  letter-spacing: -0.02em;
}

.other-settings-tab__subtitle {
  margin-top: 2px;
  font-size: 0.6875rem;
  color: var(--y-text-muted);
}

.other-settings-tab__form-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
</style>
